import {
	MouseEvent,
	ReactElement,
	ReadonlyRefObject,
	useEffect,
	useState
} from "react";
import { WelcomeStep } from "./WelcomeStep/WelcomeStep";
import { localize } from "client_legacy/ndaw/culture";
import {
	BusyIndicator,
	Form,
	FormItem,
	Toggle,
	ValidationError
} from "client/Components";
import {
	SaveUserPreferencesModel,
	UserPreferencesViewModel,
	getUserPreferences,
	saveUserPreferences
} from "client/Api/WelcomeScreen/welcomeScreen";
import { Theme } from "client/Api/Branding/UserTheme/userTheme";
import { useFormHandler } from "client/Forms";
import { ThemeSelect } from "../Layout/TitleBar/UserProfileMenu/ThemeSelect";
import {
	AlertManager,
	setDocumentTheme,
	useAlerts,
	useConfiguredTheme,
	useSynchronizedRef,
	useUnmountedCancellation
} from "client/Hooks";
import { ActivityEmailKind } from "client/Api/WorkspaceManagement/workspaceManagement";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { InputValidationResult, InputValidationResultKind } from "client/Validation";
import styles from "./UserPreferencesStep.module.less";
import image from "Content/Images/user-preferences-step.svg";
import { ContentLayout } from "./ContentLayout";

export type UserPreferencesStepProps = {
	onNext(): void;
};

export const UserPreferencesStep = (props: UserPreferencesStepProps): ReactElement => {
	const {
		onNext
	} = props;

	const theme: Theme = useConfiguredTheme();

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<SaveUserPreferencesModel>({
		theme: theme,
		activityEmailKind: null
	});

	const [canEditEmailPreferences, setCanEditEmailPreferences] = useState<boolean>(false);

	const formHandler = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	const stableThemeRef: ReadonlyRefObject<Theme> = useSynchronizedRef(theme);
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);

					const model: UserPreferencesViewModel = await getUserPreferences(signal);

					setFormData({
						theme: stableThemeRef.current,
						activityEmailKind: model.activityEmailKind
					});

					setCanEditEmailPreferences(model.canChangeEmailPreferences);
					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsBusy(false);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, stableThemeRef]
	);

	const dailyEmailPreferenceChangeHandler = (newValue: boolean): void => {
		setFormData(
			formData => ({
				...formData,
				activityEmailKind: newValue
					? ActivityEmailKind.Daily
					: null
			})
		);

		formHandler.for("activityEmailKind")
			.validation
			.triggerInputFinished();
	};

	const weeklyEmailPreferenceChangeHandler = (newValue: boolean): void => {
		setFormData(
			formData => ({
				...formData,
				activityEmailKind: newValue
					? ActivityEmailKind.Weekly
					: null
			})
		);

		formHandler.for("activityEmailKind")
			.validation
			.triggerInputFinished();
	};

	const saveHandler = async (): Promise<void> => {
		if (isBusy) {
			return;
		}

		try {
			setIsBusy(true);

			const validationResult: InputValidationResult = await formHandler.validation
				.validate(globalCancellation);

			if (validationResult.kind !== InputValidationResultKind.Valid) {
				setIsBusy(false);
				return;
			}

			await saveUserPreferences(
				prepareForSave(formData, canEditEmailPreferences),
				globalCancellation
			);

			setIsBusy(false);

			if (window.mobileApp
				&& window.mobileApp.changeTheme
			) {
				window.mobileApp
					.changeTheme(formData.theme);
			}

			onNext();
		} catch(e) {
			if (isCancelledError(e)) {
				return;
			}

			setIsBusy(false);

			if (!formHandler.validation
				.tryProcessServerError(e)
			) {
				alertManager.showError(localize("_ChangesNotSaved"));
			}
		}
	};

	return (
		<>
			<WelcomeStep.Left trackingId="user-preferences-step">
				<ContentLayout
					title={localize("_PersonalizeYourWorkspace")}
					explanation={localize("_FeelFreeToTailorTheseSettingsToSuitYourPreferences")}
				>
					{isBusy && (
						<BusyIndicator />
					)}
					<Form onSubmit={saveHandler}>
						<FormItem label={localize("_Appearance")}>
							<ThemeSelect
								{
									...formHandler.for("theme")
										.attachToSelect()
										.withOnChange(setDocumentTheme)
								}
								menuTitle={localize("_Appearance")}
							/>

							<ValidationError {...formHandler.for("theme").attachToValidationError()} />
						</FormItem>

						{canEditEmailPreferences && (
							<FormItem label={localize("_EmailSummary")} isForGroup>
								<p className={styles.emailSummaryExplanation}>
									{localize("_EmailSummaryExplanation")}
								</p>
								<ToggleItem
									label={localize("_DailySummaryOfWorkspaceActivities")}
									value={formData.activityEmailKind === ActivityEmailKind.Daily}
									onChange={dailyEmailPreferenceChangeHandler}
									trackingId="daily-email-summary"
								/>
								<ToggleItem
									label={localize("_WeeklySummaryOfWorkspaceActivities")}
									value={formData.activityEmailKind === ActivityEmailKind.Weekly}
									onChange={weeklyEmailPreferenceChangeHandler}
									trackingId="weekly-email-summary"
								/>
							</FormItem>
						)}
					</Form>
					<ContentLayout.Buttons
						onContinue={saveHandler}
					/>
				</ContentLayout>
			</WelcomeStep.Left>
			<WelcomeStep.Right
				imageUrl={image}
				text={localize("_YouCanChangeYourPreferencesInTheWorkspace")}
			/>
		</>
	);
};
UserPreferencesStep.displayName = "UserPreferencesStep";

type ToggleItemProps = {
	value: boolean;
	onChange(newValue: boolean): void;
	label: string;
	trackingId: string;
};

const ToggleItem = (props: ToggleItemProps): ReactElement => {
	const {
		value,
		onChange,
		label,
		trackingId
	} = props;

	const clickHandler = (event: MouseEvent) => {
		event.preventDefault();
		onChange(!value);
	};

	return (
		<div
			className={styles.toggleWrapper}
			onClick={clickHandler}
		>
			<label className={styles.toggleLabel}>
				{label}
			</label>
			<Toggle
				value={value}
				onChange={onChange}
				trackingId={trackingId}
			/>
		</div>
	);
};
ToggleItem.displayName = "ToggleItem";

function prepareForSave(
	formData: SaveUserPreferencesModel,
	canEditEmailPreferences: boolean
): SaveUserPreferencesModel {
	if (!canEditEmailPreferences) {
		formData = {
			...formData,
			activityEmailKind: null
		};
	}

	return formData;
}